import React from 'react'
import { css, cx } from '@emotion/css'
import { theme as deprecatedTheme } from '../../styled'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { HeaderBar } from '../../legacy-design-system/header-bar'
import { FormattedMessage } from 'react-intl'
import {
  OnboardingPanel,
  OnboardingStepsKeys,
  OnboardingStepsBreakdown
} from './onboarding-panel'
import { ProgressThermometer } from '../../legacy-design-system/thermometer'
import { Link } from '@contrast-security-inc/design-system-typography'

interface Props {
  readonly organizationID: string
  readonly applicationID?: string
  readonly breakdown: OnboardingStepsBreakdown
}

const headerBarCss = css`
  -webkit-box-shadow: 0px 3px 4px rgba(0, 0, 0, 0.16);
  -moz-box-shadow: 0px 3px 4px rgba(0, 0, 0, 0.16);
  box-shadow: 0px 3px 4px rgba(0, 0, 0, 0.16);
`

const headerContentCss = css`
  align-items: center;
  display: flex;
  padding: 5px 15px;
`
const upgradeLinkCss = css`
  margin-left: 10px;
  font-weight: ${deprecatedTheme.fontWeights.primary.bold};
  color: ${theme.color.teal50};
  &:hover {
    color: ${theme.color.teal50};
  }
`

const messageCss = css`
  font-size: 12px;
  color: ${theme.color.white};
`

const completedMessageCss = cx(
  messageCss,
  css`
    margin-left: 8px;
  `
)

const setupContainerCss = css`
  flex: 1 1 auto;
`

const setupCss = css`
  margin-left: 10px;
  color: ${theme.color.teal50};
  &:hover {
    color: ${theme.color.teal50};
  }
`
const thermometerContainerCss = css`
  width: 250px;
`

export function OnboardingHeader({
  breakdown,
  organizationID,
  applicationID
}: Props) {
  const steps = OnboardingStepsKeys.filter((k) => breakdown[k])
  const stepStatuses = steps.map((k) => breakdown[k])
  const completedSteps = stepStatuses.filter((k) => k === 'COMPLETED')
  const defaultProgress = 0.5

  const allStepsCompleted =
    steps.length !== 0 && completedSteps.length === steps.length

  return (
    <HeaderBar className={headerBarCss}>
      <div className={headerContentCss}>
        <span className={thermometerContainerCss}>
          <ProgressThermometer
            progress={
              completedSteps.length > 0
                ? completedSteps.length
                : defaultProgress
            }
            maxProgress={steps.length}
            fillColor="green50"
            bgColor="neutral100"
            renderProgress={({ width, className }) => {
              return <div className={className} style={{ width }} />
            }}
          />
        </span>

        <span className={setupContainerCss}>
          <OnboardingPanel
            organizationID={organizationID}
            applicationID={applicationID}
            breakdown={breakdown}
          >
            {({ ref, onClick }) => {
              let targetMessage

              // eslint-disable-next-line prefer-const
              targetMessage = allStepsCompleted
                ? {
                    id: 'action_onboardingHeader_completedSetup',
                    defaultMessage: 'Setup Complete'
                  }
                : {
                    id: 'action_onboardingHeader_completeSetup',
                    defaultMessage: 'Complete Setup'
                  }

              return (
                <Link
                  className={cx(
                    setupCss,
                    css`
                      font-size: 12px;
                    `
                  )}
                  ref={ref}
                  onClick={onClick}
                  children={<FormattedMessage {...targetMessage} />}
                />
              )
            }}
          </OnboardingPanel>
          {allStepsCompleted && (
            <span className={completedMessageCss}>
              <FormattedMessage
                id="detail_wayToGo_feedbackMessage"
                defaultMessage="Way to go!"
              />
            </span>
          )}
        </span>

        <span className={messageCss}>
          <FormattedMessage
            id="action_onboardingHeader_unlockFeatures"
            defaultMessage="Unlock a ton more features!"
          />
        </span>

        <Link
          className={cx(
            upgradeLinkCss,
            css`
              font-size: 12px;
            `
          )}
          isExternal
          href="https://www.contrastsecurity.com/upgrade"
        >
          <FormattedMessage id="action_upgrade" defaultMessage="Upgrade" />
        </Link>
      </div>
    </HeaderBar>
  )
}
